import type { PulseChart } from '@shared/chart';
import { drawHazardShape } from './pulseDrawShapes';
import { sectionTint } from './pulseRenderColour';

/**
 * Stateless drawers for "track" elements that scroll past the player:
 * hazards, structural section bands + labels, and the run's goal line.
 * Each helper takes the viewport width so it can self-cull anything
 * far off-screen — saves a lot of canvas calls on long maps.
 */

export function drawHazards(
  ctx: CanvasRenderingContext2D,
  chart: PulseChart,
  beat: number,
  playerX: number,
  ceilingY: number,
  floorY: number,
  xPerBeat: number,
  scaleY: number,
  viewWidth: number,
): void {
  for (const h of chart.hazards) {
    if (h.endBeat < beat - 1) {
      continue;
    }
    if (h.startBeat > beat + viewWidth / xPerBeat + 2) {
      continue;
    }
    // Pads scroll right→left toward the player on the left side of the
    // canvas, so future beats sit at HIGHER x and past beats at LOWER x.
    // The shape drawer wants leftX <= rightX, so the time-EARLIER edge is
    // the visual LEFT edge.
    const leftX = playerX + (h.startBeat - beat) * xPerBeat;
    const rightX = playerX + (h.endBeat - beat) * xPerBeat;
    const baseY = h.surface === 'floor' ? floorY : ceilingY;
    drawHazardShape(ctx, h, leftX, rightX, baseY, scaleY, chart.palette.hazard);
  }
}

export function drawSectionBands(
  ctx: CanvasRenderingContext2D,
  chart: PulseChart,
  beat: number,
  playerX: number,
  ceilingY: number,
  floorY: number,
  xPerBeat: number,
  viewWidth: number,
): void {
  for (const sec of chart.sections) {
    if (sec.endBeat < beat - 1) {
      continue;
    }
    if (sec.startBeat > beat + viewWidth / xPerBeat + 1) {
      continue;
    }
    const colour = sectionTint(sec, chart.palette);
    if (!colour) {
      continue;
    }
    // Time-earlier edge is the visual LEFT edge (pads scroll right→left).
    const x0 = playerX + (sec.startBeat - beat) * xPerBeat;
    const x1 = playerX + (sec.endBeat - beat) * xPerBeat;
    ctx.save();
    ctx.globalAlpha = 0.08;
    ctx.fillStyle = colour;
    ctx.fillRect(x0, ceilingY - 4, x1 - x0, floorY - ceilingY + 8);
    ctx.restore();
  }
}

export function drawSectionLabels(
  ctx: CanvasRenderingContext2D,
  chart: PulseChart,
  beat: number,
  playerX: number,
  ceilingY: number,
  xPerBeat: number,
  viewWidth: number,
  viewHeight: number,
): void {
  ctx.save();
  ctx.font = 'bold 11px monospace';
  ctx.textAlign = 'center';
  for (const sec of chart.sections) {
    if (sec.endBeat < beat - 1) {
      continue;
    }
    if (sec.startBeat > beat + viewWidth / xPerBeat + 1) {
      continue;
    }
    // Section labels mark the time-START boundary. Future sections sit at
    // higher x than the player (right of playerX) and scroll left.
    const x = playerX + (sec.startBeat - beat) * xPerBeat;
    ctx.globalAlpha = 0.35;
    ctx.fillStyle = chart.palette.ui;
    ctx.fillRect(x, ceilingY, 1, viewHeight - ceilingY);
    ctx.globalAlpha = 0.65;
    ctx.fillText(sec.kind.toUpperCase(), x, ceilingY - 6);
  }
  ctx.restore();
}

export function drawGoal(
  ctx: CanvasRenderingContext2D,
  chart: PulseChart,
  beat: number,
  playerX: number,
  ceilingY: number,
  floorY: number,
  xPerBeat: number,
  viewWidth: number,
): void {
  // The goal sits at chart.totalBeats — always in the future until the run
  // ends — so it lives to the RIGHT of playerX and scrolls left toward it.
  const goalX = playerX + (chart.totalBeats - beat) * xPerBeat;
  if (goalX < -50 || goalX > viewWidth + 50) {
    return;
  }
  ctx.save();
  ctx.shadowColor = chart.palette.goal;
  ctx.shadowBlur = 24;
  ctx.fillStyle = chart.palette.goal;
  ctx.fillRect(goalX, ceilingY, 4, floorY - ceilingY);
  for (let yy = ceilingY + 8; yy < floorY; yy += 18) {
    ctx.fillRect(goalX - 2, yy, 8, 6);
  }
  ctx.restore();
}
